import { nodeToJSON } from '@common/prosemirror/json/prosemirror-json';
import { ProseMirrorNodeJSON } from '@common/prosemirror/model/json';
import { CustomError } from '@common/util/custom-error';
import { diffChars } from 'diff';
import { AddOperation, applyPatch, compare, Operation, RemoveOperation, ReplaceOperation } from 'fast-json-patch';
import { isEqual } from 'lodash';
import { ProseMirrorNode, Schema } from 'prosemirror-model';
import { EditorState, Transaction } from 'prosemirror-state';
import { ReplaceStep, Step, Transform } from 'prosemirror-transform';

/*
 * The code in this file was originally based on https://gitlab.com/mpapp-public/prosemirror-recreate-steps
 * Much of the code has been rewritten to be faster and handle larger documents.
 * There are still some functions from the original code that are used in this file.
 */

/**
 * Checks if a string is a number.
 * @param value The value to check.
 * @returns True if the value is a number, false otherwise.
 */
function isNumber(value: string): boolean {
  return !isNaN(parseInt(value, 10));
}

/**
 * Gets the replace step between two documents.
 * @param fromDoc The document to replace from.
 * @param toDoc The document to replace to.
 * @returns The replace step or false if no step can be made.
 */
function getReplaceStep(fromDoc: ProseMirrorNode, toDoc: ProseMirrorNode): ReplaceStep | false {
  let start = toDoc.content.findDiffStart(fromDoc.content);
  if (start === null) {
    return false;
  }

  let {
    a: endA,
    b: endB
  } = toDoc.content.findDiffEnd(fromDoc.content);

  const overlap = start - Math.min(endA, endB);

  if (overlap > 0) {
    // If there is an overlap, there is some freedom of choice in how to calculate the start/end boundary.
    // for an inserted/removed slice. We choose the extreme with the lowest depth value.
    if (fromDoc.resolve(start - overlap).depth < toDoc.resolve(endA + overlap).depth) {
      start -= overlap
    } else {
      endA += overlap
      endB += overlap
    }
  }

  return new ReplaceStep(start, endB, toDoc.slice(start, endA));
}

/**
 * Simplifies the transform by merging adjacent steps that can be merged.
 * @param transform The transform to simplify.
 * @returns The simplified transform.
 */
function simplifyTransform(transform: Transform): Transform {
  if (!transform.steps.length) {
    return transform;
  }

  const newTr = new Transform(transform.docs[0]);
  const oldSteps = transform.steps.slice();

  while (oldSteps.length > 0) {
    let step = oldSteps.shift();

    while (oldSteps.length > 0 && step.merge(oldSteps[0])) {
      const addedStep = oldSteps.shift();
      if (step instanceof ReplaceStep && addedStep instanceof ReplaceStep) {
        step = getReplaceStep(newTr.doc, addedStep.apply(step.apply(newTr.doc).doc).doc) as ReplaceStep;
      } else {
        step = step.merge(addedStep);
      }
    }

    newTr.step(step);
  }

  return newTr;
}

/**
 * Gets the next operations that act on the same node.
 * Removes the next operations from the ops array.
 * @param ops The operations to get the next operations from.
 * @returns The next operations that act on the same node.
 */
function getNextOperationsOnSameNode(ops: Operation[]): Operation[] {
  const nextOperations: Operation[] = [];
  const nodePath = ops[0]?.path.split('/').slice(0, -1).join('/');

  while (ops.length > 0) {
    const op = ops.shift();
    nextOperations.push(op);

    if (ops.length > 0) {
      const nextPathParts = ops[0].path.split('/');
      if (nextPathParts.slice(0, -1).join('/') === nodePath) {
        continue;
      } else {
        break;
      }
    }
  }

  return nextOperations;
}

/**
 * Creates a transformation to change one document to another.
 * Simply replaces the entire document with the new one.
 * @param fromDoc The document to change from.
 * @param toDoc The document to change to.
 * @returns The transform that can be applied to the document to get the new document.
 */
function applyFullReplace(fromDoc: ProseMirrorNode, toDoc: ProseMirrorNode): Transform {
  const transform = new Transform(fromDoc);
  transform.replaceWith(0, fromDoc.content.size, toDoc);
  return transform;
}

/**
 * Finds a node at the given path.
 * @param node The node to search in.
 * @param pathParts The path to search for.
 * @returns The found node, its path index, and its start position in the document.
 */
function findNodeAtPath(node: ProseMirrorNode, pathParts: string[]): { node: ProseMirrorNode, pathIndex: number, start: number } {
  let currentNode = node;
  let pathIndex: number;
  let start = 0;

  pathParts = pathParts.slice(0, -1);

  for (let i = 0; i < pathParts.length; i += 1) {
    const part = pathParts[i];
    if (part === '') {
      continue;
    }

    // If this path is into the children of a node
    if (part === 'content') {
      if (i + 1 < pathParts.length) {
        const index = parseInt(pathParts[i + 1], 10);
        // Increment the start by 1 to account for the current node's start token (do not include the root node as it's tokens are not a part of the document size)
        if (currentNode.type.name !== 'doc') {
          start += 1;
        }

        // Increment the start by the size of all previous children
        for (let j = 0; j < index; j += 1) {
          start += currentNode.child(j).nodeSize;
        }

        currentNode = currentNode.child(index);
        i += 1;
        pathIndex = i;
      }
    } else {
      break;
    }
  }

  return {
    node: currentNode,
    pathIndex,
    start
  };
}

/**
 * Finds the parent node at the given path.
 * @param node The node to search in.
 * @param pathParts The path to search to search for.
 * @param childPathIndex The index of the child in the path parts.
 * @returns The parent node and the index of the child in the parent node.
 */
function findParentNode(node: ProseMirrorNode, childNode: ProseMirrorNode, pathParts: string[], childPathIndex: number): { childIndex: number, parentNode: ProseMirrorNode } {
  const parentPathParts = pathParts.slice(0, childPathIndex);
  const parentNode = findNodeAtPath(node, parentPathParts).node;

  let childIndex: number;
  for (let i = 0; i < parentNode.childCount; i += 1) {
    if (parentNode.child(i).eq(childNode)) {
      childIndex = i;
      break;
    }
  }

  return { childIndex, parentNode };
}

/**
 * Adds steps to the transform by applying the smallest possible changes to a text node.
 * Uses a text diffing algorithm to find the differences.
 * @param transform The transform to apply the steps to.
 * @param op The operation to apply to the text node.
 * @param node The text node to apply the operation to.
 * @param start The start position of the text node in the document.
 * @param schema The schema the node is a part of.
 */
function applyTextReplace(transform: Transform, op: ReplaceOperation<string>, node: ProseMirrorNode, start: number, schema: Schema) {
  const marks = node.marks;
  const fromText = node.text;
  const toText = op.value;
  const textDiffs = diffChars(fromText, toText);

  let offset = start; // + 1; // +1 to account for the start token of the node

  while (textDiffs.length) {
    const diff = textDiffs.shift();
    if (diff.added) {
      if (textDiffs.length && textDiffs[0].removed) {
        const nextDiff = textDiffs.shift();
        transform.replaceWith(
          offset,
          offset + nextDiff.value.length,
          schema.nodeFromJSON({ type: 'text', text: diff.value }).mark(marks)
        );
      } else {
        transform.insert(
          offset,
          schema.nodeFromJSON({ type: 'text', text: diff.value }).mark(marks)
        );
      }
      offset += diff.value.length;
    } else if (diff.removed) {
      if (textDiffs.length && textDiffs[0].added) {
        const nextDiff = textDiffs.shift();
        transform.replaceWith(
          offset,
          offset + diff.value.length,
          schema.nodeFromJSON({ type: 'text', text: nextDiff.value }).mark(marks)
        );
        offset += nextDiff.value.length;
      } else {
        transform.delete(offset, offset + diff.value.length);
      }
    } else {
      offset += diff.value.length;
    }
  }
}

/**
 * Checks if the operations are all node insertions.
 * @param operations The operations to check.
 * @returns True if the operations are all node insertions, false otherwise.
 */
function isNodeInsertions(operations: Operation[]): operations is AddOperation<ProseMirrorNodeJSON>[] {
  return operations.every(op => {
    if (op.op === 'add') {
      const pathParts = op.path.split('/');
      return pathParts.at(-2) === 'content' && isNumber(pathParts.at(-1));
    }
    return false;
  });
}

/**
 * Adds steps to the transform by applying a node insertion.
 * @param transform The transform to apply the steps to.
 * @param op The operation to apply to the node.
 * @param node The node to apply the operation to. This is the parent node of the node being inserted.
 * @param start The start position of the node in the document. This is the parent node's start position.
 * @param insertAtIndex The child index to insert the node at.
 * @param schema The schema the node is a part of.
 */
function applyNodeInsertion(transform: Transform, op: AddOperation<ProseMirrorNodeJSON>, node: ProseMirrorNode, start: number, insertAtIndex: number, schema: Schema) {
  let offset = 1; // +1 to account for the start token of the parent node

  // Increase the offset by the size of all previous children
  for (let i = 0; i < insertAtIndex; i += 1) {
    offset += node.child(i).nodeSize;
  }

  // Insert the new node at the correct position
  transform.insert(start + offset, schema.nodeFromJSON(op.value));
}

/**
 * Checks if the operations are all node removals.
 * @param operations The operations to check.
 * @returns True if the operations are all node removals, false otherwise.
 */
function isNodeRemovals(operations: Operation[]): operations is RemoveOperation[] {
  return operations.every(op => {
    if (op.op === 'remove') {
      const pathParts = op.path.split('/');
      return pathParts.at(-2) === 'content' && isNumber(pathParts.at(-1));
    }
    return false;
  });
}

/**
 * Adds steps to the transform by applying a node removal.
 * @param transform The transform to apply the steps to.
 * @param op The operation to apply to the node.
 * @param node The node to apply the operation to. This is the parent node of the node being removed.
 * @param start The start position of the node in the document. This is the parent node's start position.
 * @param removeAtIndex The child index to remove the node at.
 * @param schema The schema the node is a part of.
 */
function applyNodeRemoval(transform: Transform, op: RemoveOperation, node: ProseMirrorNode, start: number, removeAtIndex: number, schema: Schema) {
  let offset = 1; // +1 to account for the start token of the parent node

  // Increase the offset by the size of all previous children
  for (let i = 0; i < removeAtIndex; i += 1) {
    offset += node.child(i).nodeSize;
  }

  // Remove the node at the correct position
  transform.delete(start + offset, start + offset + node.child(removeAtIndex).nodeSize);
}

/**
 * Checks if the operations are all node markup changes.
 * @param operations The operations to check.
 * @returns True if the operations are all node markup changes, false otherwise.
 */
function isNodeMarkupChanges(operations: Operation[]): boolean {
  return operations.every(op => {
    const pathParts = op.path.split('/');
    return pathParts.includes('attrs') || pathParts.includes('type');
  });
}

/**
 * Adds steps to the transform by applying the operations to the node.
 * @param transform The transform to apply the steps to.
 * @param operations The operations to apply to the node.
 * @param schema The schema the node is a part of.
 */
function applyOperationsToNode(transform: Transform, operations: Operation[], schema: Schema) {
  const pathParts = operations[0].path.split('/');
  const { node, pathIndex, start } = findNodeAtPath(transform.doc, pathParts);

  // Grab the node's JSON to apply the operations to
  const nodeJSON: ProseMirrorNodeJSON = nodeToJSON(node);

  // Apply the operations to the node's JSON.
  // The operations are for the full doc so change the paths to be relative to the node.
  applyPatch(nodeJSON, operations.map(op => {
    return {
      ...op,
      path: '/' + op.path.split('/').slice(pathIndex + 1).join('/')
    };
  }));

  // Optimization: If this change is only affecting the node's type or attrs, then just set the node markup
  if (nodeJSON.type !== 'text' && isNodeMarkupChanges(operations)) {
    // Make the new node from the new JSON
    const newNode = schema.nodeFromJSON(nodeJSON);
    transform.setNodeMarkup(start, newNode.type, newNode.attrs, newNode.marks);
  }
  // Optimization: if this change is only affecting the node's text, then apply text diffing to find the smallest possible change
  else if (operations.length === 1 && operations[0].op === 'replace' && pathParts.at(-1) === 'text') {
    applyTextReplace(transform, operations[0], node, start, schema);
  }
  // Optimization: if this change is only adding a node to the content, then just insert the node at the correct position instead of replacing the parent node
  else if (isNodeInsertions(operations)) {
    operations.forEach(op => {
      const pathParts = op.path.split('/');
      const { node, start } = findNodeAtPath(transform.doc, pathParts);
      applyNodeInsertion(transform, op, node, start, parseInt(pathParts.at(-1), 10), schema);
    });
  }
  // Optimization: if this change is only removing a node from the content, then just remove the node at the correct position instead of replacing the parent node
  else if (isNodeRemovals(operations)) {
    operations.forEach(op => {
      const pathParts = op.path.split('/');
      const { node, start } = findNodeAtPath(transform.doc, pathParts);
      applyNodeRemoval(transform, op, node, start, parseInt(pathParts.at(-1), 10), schema);
    });
  } else {
    // Make the new node from the new JSON
    const newNode = schema.nodeFromJSON(nodeJSON);

    // Grab the parent node to check if the replacement node is valid
    const { childIndex, parentNode } = findParentNode(transform.doc, node, operations[0].path.split('/'), pathIndex);

    // Check if the node can be replaced
    if (!parentNode.canReplaceWith(childIndex, childIndex, newNode.type, newNode.marks) && node.type !== newNode.type) {
      // TODO: instead of bailing and relying on a full doc replace can we just remove this node entirely right here and then let it get fixed on the next compare?
      throw new CustomError('Operation Cannot Be Applied', 'Cannot create next step')
    }

    // Replace the node
    transform.replaceWith(start, start + node.nodeSize, newNode);
  }
}

/**
 * Creates a transformation to change one document to another.
 * Uses a JSON diffing algorithm to find the differences between the two documents and applies them as steps to the transform.
 * @param fromDoc The document to change from.
 * @param toDoc The document to change to.
 * @returns The transform that can be applied to the fromDoc to get to the toDoc.
 */
function applyJSONPatch(fromDoc: ProseMirrorNode, toDoc: ProseMirrorNode): Transform {
  // 1. Generate json patch operations
  // 2. Grab first set of operations that act on the same node
  // 3. Apply the json patch operations to the JSON version of the node
  // 4. Replace the node in the doc with a new node made from the JSON
  // 5. Repeat until all operations are applied

  const schema = fromDoc.type.schema;
  const fromJSON = nodeToJSON(fromDoc);
  const toJSON = nodeToJSON(toDoc);
  const transform = new Transform(fromDoc);
  let ops = compare(fromJSON, toJSON);

  // Loop through the operations and apply them to the doc until there are no more operations left
  while (ops.length > 0) {
    const oldOps = [...ops];
    // Grab the set of operations that all act on the same next node
    const nextOperations = getNextOperationsOnSameNode(ops);

    // Apply the operations to the node
    applyOperationsToNode(transform, nextOperations, schema);

    // TODO: it may be possible to not have to compare the docs again. There are some failing tests but it would be a huge optimization if we can remove this step.
    // After a node is changed, get new operations for the current doc
    const newOps = compare(nodeToJSON(transform.doc), toJSON);

    // Detect an infinite loop where the operations are the same as the previous ones
    if (isEqual(oldOps, newOps)) {
      throw new CustomError('Operation Cannot Be Applied', 'Unable to change doc with operations')
    }

    ops = newOps;
  }

  return transform;
}

/**
 * Creates a transform to get from one document to another.
 * This function will first try to apply a JSON patch to the document.
 * If that fails, it will fall back to a full document replace.
 * @param fromDoc The document to change from.
 * @param toDoc The document to change to.
 * @returns The transform that can be applied to the fromDoc to get to the toDoc.
 */
export function recreateTransform(fromDoc: ProseMirrorNode, toDoc: ProseMirrorNode): Transform {
  let transform: Transform;

  try {
    transform = applyJSONPatch(fromDoc, toDoc);
  } catch (ex) {
    // throw ex; // TODO: remove this after testing
    if (ex.name === 'Operation Cannot Be Applied') {
      transform = applyFullReplace(fromDoc, toDoc);
    } else {
      throw ex;
    }
  }

  return simplifyTransform(transform);
}

/**
 * Creates a transaction from the given state and adds the steps to it.
 * @param editorState The state of the editor.
 * @param steps The steps to apply to the transaction.
 * @returns The transaction with the steps added to it.
 */
export function createTransactionForState(editorState: EditorState, steps: Step[]): Transaction {
  const tr = editorState.tr;

  for (const step of steps) {
    tr.step(step);
  }

  return tr;
}
